import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Boxes, ChevronUp, ChevronDown, Pencil, Trash2 } from "lucide-react";
import { EmptyState } from "@/components/common/EmptyState";
import { EntryModal } from "@/components/common/EntryModal";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useResource } from "@/hooks/useResource";
import { useUser } from "@/context/UserContext";
import { OWNERS, ITEM_TYPES, PRODUCTION_STATUS, ITEM_PRODUCTION_AREAS, productBadge } from "@/data/productOptions";

const fields = (chapterId) => [
  { name: "chapter_id", label: "chapter_id", type: "hidden" },
  { name: "name", label: "Item name", type: "text", placeholder: "e.g. Meet Captain Hightides", required: true },
  { name: "item_type", label: "Item type", type: "select", placeholder: "Type", options: ITEM_TYPES, required: true },
  { name: "owner", label: "Owner", type: "select", placeholder: "Owner", options: OWNERS },
  { name: "due_date", label: "Due date", type: "date" },
  { name: "description", label: "Short description", type: "textarea", placeholder: "What is this item?" },
  { name: "learning_fact", label: "Learning fact", type: "textarea", placeholder: "The key fact learners take away" },
  ...ITEM_PRODUCTION_AREAS.map((a) => ({ name: a.key, label: a.label, type: "select", placeholder: a.label, options: PRODUCTION_STATUS })),
  { name: "current_blocker", label: "Current blocker", type: "textarea" },
  { name: "next_action", label: "Next action", type: "textarea" },
  { name: "notes", label: "Notes", type: "textarea" },
].filter((f) => f.type !== "hidden");

export const ContentItemsPanel = ({ chapterId, onChange }) => {
  const { canWrite } = useUser();
  const { items: all, refetch } = useResource("content-items");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [ownerF, setOwnerF] = useState("all");
  const [typeF, setTypeF] = useState("all");

  const items = useMemo(() => {
    let rows = all.filter((i) => i.chapter_id === chapterId);
    if (ownerF !== "all") rows = rows.filter((i) => i.owner === ownerF);
    if (typeF !== "all") rows = rows.filter((i) => i.item_type === typeF);
    return rows;
  }, [all, chapterId, ownerF, typeF]);

  const openAdd = () => { setEditing({ chapter_id: chapterId, item_type: "Discovery item", owner: "Steven" }); setModalOpen(true); };
  const openEdit = (it) => { setEditing(it); setModalOpen(true); };

  const handleSave = async (v) => {
    if (v.id) { await api.update("content-items", v.id, v); toast.success("Item updated"); }
    else { await api.create("content-items", { ...v, chapter_id: chapterId }); toast.success("Item added"); }
    await refetch(); onChange?.();
  };

  const move = async (index, dir) => {
    const chapterItems = all.filter((i) => i.chapter_id === chapterId);
    const arr = [...chapterItems];
    const target = index + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    await api.reorder("content-items", arr.map((i) => i.id));
    await refetch();
  };

  const doDelete = async (reason) => { await api.remove("content-items", confirmId, reason); toast.success("Moved to Recycle Bin"); await refetch(); onChange?.(); setConfirmId(null); };

  return (
    <div className="mt-3 rounded-2xl border border-[#E4E9F3] bg-[#F7F9FE] p-4" data-testid={`items-panel-${chapterId}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-[#7C3AED]"><Boxes size={14} strokeWidth={2.2} /> Content Items</span>
        <div className="flex items-center gap-2">
          <MiniSelect testid={`items-filter-owner-${chapterId}`} value={ownerF} onChange={setOwnerF} label="Owner" options={OWNERS} />
          <MiniSelect testid={`items-filter-type-${chapterId}`} value={typeF} onChange={setTypeF} label="Type" options={ITEM_TYPES} />
          {canWrite && <Button size="sm" data-testid={`add-item-btn-${chapterId}`} onClick={openAdd} className="h-8 rounded-full bg-[#7C3AED] text-white hover:bg-[#6D28D9]"><Plus size={14} /> Add Item</Button>}
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={Boxes} title="No content items yet" description="Add discovery items, missions, quizzes and rewards to this chapter." actionLabel="Add item" onAction={openAdd} />
      ) : (
        <ul className="space-y-2" data-testid={`items-list-${chapterId}`}>
          {items.map((it, i) => (
            <li key={it.id} className="flex items-center gap-3 rounded-xl border border-[#E4E9F3] bg-white px-3 py-2">
              {canWrite && (
              <div className="flex flex-col">
                <button data-testid={`item-up-${it.id}`} disabled={i === 0} onClick={() => move(i, -1)} className="text-[#9DAAC6] disabled:opacity-30 hover:text-[#14245F]"><ChevronUp size={14} /></button>
                <button data-testid={`item-down-${it.id}`} disabled={i === items.length - 1} onClick={() => move(i, 1)} className="text-[#9DAAC6] disabled:opacity-30 hover:text-[#14245F]"><ChevronDown size={14} /></button>
              </div>
              )}
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[#7C3AED]/10 font-numeric text-[11px] font-bold text-[#7C3AED]">{it.item_number}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-[#14245F]">{it.name}</p>
                <p className="text-xs text-[#5A6B8C]">{it.item_type} · {it.owner}</p>
              </div>
              {canWrite && <Button size="icon" variant="ghost" data-testid={`edit-item-${it.id}`} onClick={() => openEdit(it)} className="h-7 w-7 rounded-full text-[#5A6B8C] hover:text-[#14245F]"><Pencil size={14} /></Button>}
              {canWrite && <Button size="icon" variant="ghost" data-testid={`delete-item-${it.id}`} onClick={() => setConfirmId(it.id)} className="h-7 w-7 rounded-full text-[#5A6B8C] hover:text-[#DC2626]"><Trash2 size={14} /></Button>}
            </li>
          ))}
        </ul>
      )}

      <EntryModal open={modalOpen} onOpenChange={setModalOpen} title={editing?.id ? "Edit Content Item" : "Add Content Item"} submitLabel={editing?.id ? "Save Changes" : "Add Item"} fields={fields(chapterId)} initialValues={editing} onSubmit={handleSave} />
      <ConfirmDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)} title="Delete this content item?" softDelete onConfirm={doDelete} />
    </div>
  );
};

const MiniSelect = ({ testid, value, onChange, label, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="h-8 w-auto min-w-[100px] border-[#E5E2D9] bg-white text-xs"><SelectValue placeholder={label} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{label}: All</SelectItem>
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);
